/*******************************************************
 * Product Addons Script
 *
 * - Updates addon price, SKU, image, and UI when an option is changed.
 * - Handles checkbox toggling for addons.
 * - Calculates the total price (base product + addons).
 * - Listens for custom events to add addons to the Shopify cart.
 *******************************************************/

/**
 * Cache the main addons container element.
 */
const addonsEl = document.querySelector('#product-addons');

/**
 * Event listener for change events on each addon option selector.
 * When an option is selected:
 * - Update price, SKU, and image for the corresponding addon.
 * - Mark the addon as selected (enable its input, add 'checked-box' class).
 * - Trigger a recalculation of the total price.
 */
document.querySelectorAll('.addon-option-selector').forEach(selectElement => {
  selectElement.addEventListener('change', function () {
    // Get the selected option and its data attributes.
    const selectedOption = this.options[this.selectedIndex];
    const productId = selectedOption.getAttribute('data-product-id');
    const productEl = addonsEl.querySelector(`.product-addon[data-product-id="${productId}"]`);
    const inputEl = productEl.querySelector('input');

    const imageId = selectedOption.getAttribute('data-image-id');
    const price = parseFloat(selectedOption.getAttribute('data-price'));
    const compareAtPrice = parseFloat(selectedOption.getAttribute('data-compare-at-price'));
    const sku = selectedOption.getAttribute('data-sku');

    // Format prices (assuming prices are provided in cents)
    const priceFormatted = `$${(price / 100).toFixed(2)} CAD`;
    const compareAtPriceFormatted = `$${(compareAtPrice / 100).toFixed(2)} CAD`;

    // Enable the addon input and visually mark it as checked.
    productEl.classList.add('checked-box');
    inputEl.removeAttribute('disabled');
    inputEl.checked = true;

    // Update the addon's price in the DOM.
    const priceEl = document.querySelector(`#product-addon-price-${productId}`);
    if (priceEl) {
      priceEl.textContent = priceFormatted;
    }

    // Update SKU if available.
    if (sku) {
      const skuEl = productEl.querySelector('.product-meta__sku');
      if (skuEl) {
        skuEl.innerText = `Part No: ${sku}`;
      }
    }

    // Log the selection (distinguish between a placeholder and a valid option).
    if (selectedOption.classList.contains('placeholder')) {
      console.log('Placeholder is selected');
    } else {
      console.log(`Selected option: ${selectedOption.textContent}`);
    }

    // Update compare-at-price if applicable.
    const compareAtPriceEl = document.querySelector(`#product-addon-compare-at-price-${productId}`);
    if (compareAtPriceEl) {
      compareAtPriceEl.textContent = (compareAtPrice > price) ? compareAtPriceFormatted : '';
    }

    // Update the addon product image if the update function exists.
    if (typeof update_image_for_addon_product === 'function') {
      update_image_for_addon_product(productId, imageId);
    }

    // Recalculate total price including addons.
    if (typeof show_total_price === 'function') {
      show_total_price();
    }
  });
});

/**
 * Event listener for addon checkbox toggles.
 * Adds or removes the 'checked-box' class and recalculates the total price.
 */
document.querySelectorAll('input[type=checkbox].product-addon-checkbox').forEach(checkbox => {
  checkbox.addEventListener('change', function () {
    // Toggle the visual 'checked-box' class.
    if (this.checked) {
      this.parentElement.classList.add('checked-box');
    } else {
      this.parentElement.classList.remove('checked-box');
    }
    // Recalculate the total price whenever an addon is checked/unchecked.
    show_total_price();
  });
});

/**
 * Listen for a custom event triggered from the product form
 * that signals addons should be added to the cart.
 */

// document.addEventListener('on:cart:addon:add', function () {
//   addAddonsToCart();
// });
// Listen for the addon event and then process addon updates.
document.addEventListener('on:cart:addon:add', async () => {
  try {
    // Wait for the addons update to finish
    await addAddonsToCart();
    document.documentElement.dispatchEvent(
      new CustomEvent('on:addons:complete', { bubbles: true })
    );
    // Once done, dispatch on:line-item:change:end so that cart refreshes occur
    document.documentElement.dispatchEvent(
      new CustomEvent('on:line-item:change:end', { bubbles: true })
    );
  } catch (error) {
    console.error('Error in addon update:', error);
    // Even on error, you might choose to dispatch the event to trigger a refresh
    document.documentElement.dispatchEvent(
      new CustomEvent('on:line-item:change:end', { bubbles: true })
    );
  }
});


/**
 * Adds selected addon items to the Shopify cart.
 * Gathers checked addon items, builds an items array with line item properties for SKU and MFT SKU,
 * and posts to the cart endpoint.
 */
async function addAddonsToCart() {
  const items = [];

  // Gather data from all checked addon checkboxes.
  document.querySelectorAll('input[type=checkbox].product-addon-checkbox:checked').forEach(checkbox => {
    const productId = checkbox.getAttribute('data-product-id');
    const selectedOption = document.querySelector(`#addon-option-selector-${productId} option:checked`);

    // Get SKU and MFT SKU data from the selected option
    const sku = selectedOption.getAttribute('data-sku');
    const mftPartNumber = selectedOption.getAttribute('data-mft-part-number');

    // Build the item object with line item properties
    const item = {
      id: selectedOption.getAttribute('data-variant-id'),
      quantity: parseInt(checkbox.getAttribute('data-quantity'), 10),
    };

    // Add line item properties for SKU and MFT SKU if they exist
    if (sku || mftPartNumber) {
      item.properties = {};

      if (sku) {
        item.properties['Part Number'] = sku;
      }

      if (mftPartNumber) {
        item.properties['Mft Part Number'] = mftPartNumber;
      }
    }

    items.push(item);
  });

  if (!items.length) return;

  const url = window.Shopify.routes.root + 'cart/add.js';

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add items to cart');
    }

    const data = await response.json();

    console.log('Items successfully added to cart:', data);
    return data;
  } catch (error) {
    console.error('Error adding items to cart:', error);
    throw error;
  }
}

/**
 * Updates the image for a given addon product.
 * Displays the image matching the provided image_id and hides others.
 * @param {string} productId - The product ID.
 * @param {string} imageId - The image ID to display.
 */
function update_image_for_addon_product(productId, imageId) {
  // Get all images associated with the addon product.
  const images = document.querySelectorAll(`.images-for-${productId}`);
  if (!imageId) return;

  // Determine if variant images are available.
  const hasVariantImages = document.querySelector(`#addon-option-selector-${productId}`).style.display !== 'none';

  // If there's only one image or no variant images, do nothing.
  if (images.length === 1 || !hasVariantImages) return;

  // Show the image matching imageId; hide others.
  images.forEach(image => {
    image.style.display = (image.getAttribute('data-image-id') === imageId) ? 'block' : 'none';
  });
}

/**
 * Listens for variant change events to recalculate total price.
 */
document.addEventListener('on:variant:change', function () {
  show_total_price();
});

/**
 * Calculates and displays the total price including base product and selected addons.
 */
function show_total_price() {
  // Get base product price and quantity.
  let productPriceText = document.querySelector('.product-info__price .price__current').textContent;
  let productPrice = parseFloat(productPriceText.replace(/[^0-9.]/g, ''));
  const quantitySelector = document.querySelector('.qty-input .qty-input__input');
  const productQty = parseInt(quantitySelector.value, 10);

  // If quantity is invalid, clear the total price display.
  if (isNaN(productQty)) {
    document.querySelector('#total_price_with_addons span').textContent = '';
    document.querySelector('#total_price_container').classList.remove('total_price_selected');
    return;
  }

  // Collect addon data from checked checkboxes.
  const addons = [];
  document.querySelectorAll('input[type=checkbox].product-addon-checkbox:checked').forEach(checkbox => {
    const productId = checkbox.getAttribute('data-product-id');
    const selectedOption = document.querySelector(`#addon-option-selector-${productId} option:checked`);
    addons.push({
      id: selectedOption.getAttribute('data-variant-id'),
      quantity: parseInt(checkbox.getAttribute('data-quantity'), 10),
      price_each: parseFloat(selectedOption.getAttribute('data-price')) / 100,
    });
  });

  // Calculate the total price: base price * quantity + sum(addon price * quantity)
  let totalPrice = productPrice * productQty;
  addons.forEach(addon => {
    totalPrice += addon.price_each * addon.quantity;
  });

  // Update UI styling based on whether addons are selected or multiple quantities exist.
  const totalPriceContainer = document.querySelector('#total_price_container');
  if (addons.length > 0 || productQty > 1) {
    totalPriceContainer.classList.add('total_price_selected');
  } else {
    totalPriceContainer.classList.remove('total_price_selected');
  }

  // Format and display the total price.
  const priceFormatted = `$${totalPrice.toFixed(2)} CAD`;
  document.querySelector('#total_price_with_addons span').textContent = priceFormatted;
}